import { useEffect, useRef, useState } from 'react';

import { CustomCellEditorProps } from '@ag-grid-community/react';
import { filter } from 'lodash/fp';
import { Duration } from 'luxon';

import { useMads, useWorkPlaces } from '../../hooks/useSwr';
import { OdooPointing, OdooWorkPlace } from '../../types/models';
import { PointingState } from '../../utils/odooUtils';

export const WorkingHoursCellEditor = ({ value, onValueChange }: CustomCellEditorProps) => {
    // Convert float hours to HH:mm string format
    const floatToTimeString = (hours: number | null): string => {
        if (hours === null) return '';
        return Duration.fromObject({ hours }).toFormat('hh:mm');
    };

    // Convert HH:mm string to float hours
    const timeStringToFloat = (timeString: string): number | null => {
        if (!timeString) return null;
        const [hours, minutes] = timeString.split(':').map(Number);
        return hours + minutes / 60;
    };

    // Handle input changes: convert time string to float and notify parent
    const handleTimeChange = (timeString: string) => {
        const floatHours = timeStringToFloat(timeString);
        onValueChange(floatHours);
    };

    useEffect(() => {
        refInput.current?.focus();
    }, []);

    const refInput = useRef<HTMLInputElement>(null);

    return (
        <input
            type="time"
            value={floatToTimeString(value)}
            ref={refInput}
            step={900}
            onChange={(event) => handleTimeChange(event.target.value)}
            style={{ width: '100%' }}
        />
    );
};

interface WorkplaceEditorState {
    selectedWorkplaceId: number;
    otherWorkplaceText: string;
    isOtherSelected: boolean;
}

interface WorkplaceValueChangePayload {
    workplace_id: [number, string] | false;
    other_workplace: string;
    city?: string;
}

const useWorkplaceEditorState = (initialData: any) => {
    const currentWorkplaceId = initialData?.workplace_id?.[0] || 0;
    const currentOtherWorkplace = initialData?.other_workplace || '';
    
    return useState<WorkplaceEditorState>({
        selectedWorkplaceId: currentWorkplaceId,
        otherWorkplaceText: currentOtherWorkplace,
        isOtherSelected: currentWorkplaceId === 0,
    });
};

const useWorkplaceHandlers = (
    state: WorkplaceEditorState,
    setState: React.Dispatch<React.SetStateAction<WorkplaceEditorState>>,
    availableWorkplaces: OdooWorkPlace[],
    onValueChange: (value: WorkplaceValueChangePayload) => void
) => {
    const handleWorkplaceChange = (workplaceId: number) => {
        const newState = { ...state, selectedWorkplaceId: workplaceId };
        
        if (workplaceId === 0) {
            newState.isOtherSelected = true;
            setState(newState);
            onValueChange({
                workplace_id: false,
                other_workplace: state.otherWorkplaceText,
            });
        } else {
            newState.isOtherSelected = false;
            setState(newState);
            
            const selectedWorkplace = availableWorkplaces.find((w) => w.id === workplaceId);
            onValueChange({
                workplace_id: [workplaceId, selectedWorkplace?.name || ''],
                other_workplace: '',
                city: selectedWorkplace?.city || '',
            });
        }
    };

    const handleOtherWorkplaceChange = (text: string) => {
        const newState = { ...state, otherWorkplaceText: text };
        setState(newState);
        
        if (state.isOtherSelected) {
            onValueChange({
                workplace_id: false,
                other_workplace: text,
            });
        }
    };

    return { handleWorkplaceChange, handleOtherWorkplaceChange };
};

export const WorkplaceCellEditor = ({ data, onValueChange }: CustomCellEditorProps) => {
    const { dataWithPlacehoder: workPlaces } = useWorkPlaces();
    const { data: mads } = useMads();
    const containerRef = useRef<HTMLDivElement>(null);
    
    const madId = data?.mad_id?.[0] || 0;
    const availableWorkplaces = filter((w: OdooWorkPlace) => w.mad_ids.includes(madId) || w.id === 0, workPlaces);
    
    const [state, setState] = useWorkplaceEditorState(data);
    const { handleWorkplaceChange, handleOtherWorkplaceChange } = useWorkplaceHandlers(
        state,
        setState,
        availableWorkplaces,
        onValueChange
    );

    useEffect(() => {
        const selectElement = containerRef.current?.querySelector('select');
        selectElement?.focus();
    }, []);

    return (
        <div
            ref={containerRef}
            style={{
                padding: '8px',
                backgroundColor: 'white',
                border: '1px solid #ddd',
                borderRadius: '4px',
                minWidth: '300px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            }}
        >
            <div style={{ marginBottom: '8px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>Lieu de travail:</label>
                <select
                    value={state.selectedWorkplaceId}
                    onChange={(e) => handleWorkplaceChange(Number(e.target.value))}
                    style={{
                        width: '100%',
                        padding: '6px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                        fontSize: '14px',
                    }}
                >
                    {availableWorkplaces.map((workplace) => (
                        <option key={workplace.id} value={workplace.id}>
                            {workplace.name}
                        </option>
                    ))}
                </select>
            </div>

            {state.isOtherSelected && (
                <div>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: 'bold' }}>
                        Adresse du chantier:
                    </label>
                    <textarea
                        value={state.otherWorkplaceText}
                        onChange={(e) => handleOtherWorkplaceChange(e.target.value)}
                        placeholder="Veuillez indiquer l'adresse"
                        style={{
                            width: '100%',
                            minHeight: '60px',
                            padding: '6px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            fontSize: '14px',
                            resize: 'vertical',
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export const _isPointingEditable = (notNullFields: (keyof OdooPointing)[], pointing: OdooPointing) => {
    return (
        [PointingState.WaitValidation].includes(pointing.state as PointingState) &&
        notNullFields.every((field) => Boolean(pointing[field]))
    );
};

export const isPointingEditable = (notNullFields: (keyof OdooPointing)[] = []) => {
    return (params: any) => _isPointingEditable(notNullFields, params.data);
};
